// Dose rules (docs/plans/phase1-consultation.md §2.11, §5.4, §6).
//
// A dose rule is a clinical threshold, so Wodi ships none. Every rule is tenant data that
// a named person entered with a stated source, used as "Provisional" until a second person
// approves it. This module validates a rule set and picks the rules that apply to one
// subject, route and age; the arithmetic that compares a dose with them is in checks.ts.
//
// Bands follow the vital-sign rules: age from is inclusive, age to is exclusive, null
// means no upper bound. Weight bands (kg × 100) work the same way. Amounts are × 1000 in
// the rule's unit; for per-kg kinds the amount is per kilogram.

import type { ClinicalRuleApproval } from '../clinical-rules/rules.ts';
import { isMeasurableUnit } from './quantities.ts';

export const DOSE_RULE_KINDS = [
  'max_single_dose',
  'max_daily_dose',
  'per_kg_single',
  'per_kg_daily',
  'weight_band_dose',
] as const;
export type DoseRuleKind = (typeof DOSE_RULE_KINDS)[number];

export const DOSE_RULE_SUBJECT_TYPES = ['substance', 'formulary_item'] as const;
export type DoseRuleSubjectType = (typeof DOSE_RULE_SUBJECT_TYPES)[number];

export const DOSE_RULE_ENFORCEMENTS = ['interruptive', 'blocking'] as const;
export type DoseRuleEnforcement = (typeof DOSE_RULE_ENFORCEMENTS)[number];

/** Scale of `lowScaled` and `highScaled`: amount × 1000. */
export const DOSE_RULE_AMOUNT_DECIMALS = 3;
/** Scale of weight bands and weights: kg × 100. */
export const WEIGHT_DECIMALS = 2;

export interface DoseRule {
  readonly id: string;
  readonly version: number;
  readonly subjectType: DoseRuleSubjectType;
  /** A substance code, or a formulary item ID. */
  readonly subject: string;
  /** Null: any route. */
  readonly routeCode: string | null;
  readonly kind: DoseRuleKind;
  readonly ageFromDays: number;
  readonly ageToDays: number | null;
  /** kg × 100; only for `weight_band_dose`. */
  readonly weightFromScaled: number | null;
  readonly weightToScaled: number | null;
  /** Amount × 1000 in `amountUnit` (per kg for per-kg kinds). */
  readonly lowScaled: number | null;
  readonly highScaled: number | null;
  readonly amountUnit: string;
  readonly enforcement: DoseRuleEnforcement;
  readonly source: string;
  readonly approval: ClinicalRuleApproval;
}

export type DoseRuleIssueCode =
  | 'dose_rule.overlap'
  | 'dose_rule.low_above_high'
  | 'dose_rule.high_required'
  | 'dose_rule.bound_required'
  | 'dose_rule.source_required'
  | 'dose_rule.unit_invalid'
  | 'dose_rule.per_kg_form_unit'
  | 'dose_rule.weight_band_only'
  | 'dose_rule.weight_band_required'
  | 'dose_rule.age_band_invalid'
  | 'dose_rule.weight_band_invalid'
  | 'dose_rule.amount_invalid'
  | 'dose_rule.low_not_allowed';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const DOSE_RULE_ISSUES: Record<DoseRuleIssueCode, string> = {
  'dose_rule.overlap':
    'Two rules of the same kind cover the same ages (and weights) for this route. Make the bands meet without overlapping, or give each a route.',
  'dose_rule.low_above_high': 'The lower amount is above the upper amount',
  'dose_rule.high_required': 'A maximum needs an upper amount',
  'dose_rule.bound_required': 'Enter at least one amount',
  'dose_rule.source_required': 'Say where the numbers come from: guideline, edition and page',
  'dose_rule.unit_invalid': 'Choose a unit this kind of rule can use',
  'dose_rule.per_kg_form_unit':
    'A per-kilogram rule needs a measured unit such as mg, not a tablet or other form unit',
  'dose_rule.weight_band_only': 'Only a weight band rule has weights',
  'dose_rule.weight_band_required': 'A weight band rule needs the weights it covers',
  'dose_rule.age_band_invalid': 'The age band ends before it starts',
  'dose_rule.weight_band_invalid': 'The weight band ends before it starts',
  'dose_rule.amount_invalid': 'Amounts are positive numbers',
  'dose_rule.low_not_allowed': 'A maximum has only an upper amount',
};

export interface DoseRuleIssue {
  readonly code: DoseRuleIssueCode;
  readonly ruleIds: readonly string[];
}

export interface ValidateDoseRulesOptions {
  /**
   * The form units a product rule may use (the product's dose unit, such as `tablet`).
   * Substance rules only take measured units.
   */
  readonly formUnitsOf?: (rule: DoseRule) => readonly string[];
}

function bandsOverlap(
  fromA: number,
  toA: number | null,
  fromB: number,
  toB: number | null,
): boolean {
  const aEndsBeforeB = toA !== null && toA <= fromB;
  const bEndsBeforeA = toB !== null && toB <= fromA;
  return !aEndsBeforeB && !bEndsBeforeA;
}

function isPerKg(kind: DoseRuleKind): boolean {
  return kind === 'per_kg_single' || kind === 'per_kg_daily';
}

function checkOne(rule: DoseRule, options: ValidateDoseRulesOptions): DoseRuleIssueCode[] {
  const issues: DoseRuleIssueCode[] = [];
  if (rule.source.trim() === '') issues.push('dose_rule.source_required');
  if (rule.ageFromDays < 0 || (rule.ageToDays !== null && rule.ageToDays <= rule.ageFromDays)) {
    issues.push('dose_rule.age_band_invalid');
  }
  const hasWeights = rule.weightFromScaled !== null || rule.weightToScaled !== null;
  if (rule.kind === 'weight_band_dose') {
    if (rule.weightFromScaled === null) issues.push('dose_rule.weight_band_required');
    else if (
      rule.weightFromScaled < 0 ||
      (rule.weightToScaled !== null && rule.weightToScaled <= rule.weightFromScaled)
    ) {
      issues.push('dose_rule.weight_band_invalid');
    }
  } else if (hasWeights) {
    issues.push('dose_rule.weight_band_only');
  }
  for (const bound of [rule.lowScaled, rule.highScaled]) {
    if (bound !== null && (!Number.isSafeInteger(bound) || bound <= 0)) {
      issues.push('dose_rule.amount_invalid');
    }
  }
  if (rule.kind === 'weight_band_dose') {
    if (rule.lowScaled === null && rule.highScaled === null)
      issues.push('dose_rule.bound_required');
  } else if (rule.highScaled === null) {
    issues.push('dose_rule.high_required');
  }
  if (
    (rule.kind === 'max_single_dose' || rule.kind === 'max_daily_dose') &&
    rule.lowScaled !== null
  ) {
    // A maximum has only an upper amount; a lower one would read as a range nobody checks.
    issues.push('dose_rule.low_not_allowed');
  }
  if (rule.lowScaled !== null && rule.highScaled !== null && rule.lowScaled > rule.highScaled) {
    issues.push('dose_rule.low_above_high');
  }
  const measured = isMeasurableUnit(rule.amountUnit);
  if (!measured) {
    const formUnits =
      rule.subjectType === 'formulary_item' ? (options.formUnitsOf?.(rule) ?? []) : [];
    if (isPerKg(rule.kind) && formUnits.includes(rule.amountUnit)) {
      issues.push('dose_rule.per_kg_form_unit');
    } else if (isPerKg(rule.kind) || !formUnits.includes(rule.amountUnit)) {
      issues.push('dose_rule.unit_invalid');
    }
  }
  return issues;
}

/**
 * Everything wrong with a set of rules, in a stable order. Retired rules are history and
 * are not checked for overlap. Gaps between bands are allowed: a gap means "No dose limit
 * set" for those ages, which the screen says.
 */
export function validateDoseRules(
  rules: readonly DoseRule[],
  options: ValidateDoseRulesOptions = {},
): DoseRuleIssue[] {
  const issues: DoseRuleIssue[] = [];
  for (const rule of rules) {
    for (const code of new Set(checkOne(rule, options))) issues.push({ code, ruleIds: [rule.id] });
  }
  const live = rules.filter((r) => r.approval !== 'retired');
  live.forEach((a, i) => {
    for (const b of live.slice(i + 1)) {
      if (a.subjectType !== b.subjectType || a.subject !== b.subject || a.kind !== b.kind) continue;
      const routesOverlap =
        a.routeCode === null || b.routeCode === null || a.routeCode === b.routeCode;
      if (!routesOverlap) continue;
      if (!bandsOverlap(a.ageFromDays, a.ageToDays, b.ageFromDays, b.ageToDays)) continue;
      if (
        a.kind === 'weight_band_dose' &&
        !bandsOverlap(
          a.weightFromScaled ?? 0,
          a.weightToScaled,
          b.weightFromScaled ?? 0,
          b.weightToScaled,
        )
      ) {
        continue;
      }
      issues.push({ code: 'dose_rule.overlap', ruleIds: [a.id, b.id] });
    }
  });
  return issues;
}

// --- Selection --------------------------------------------------------------------------------

export interface DoseRuleQuery {
  readonly subjectType: DoseRuleSubjectType;
  readonly subject: string;
  readonly kind: DoseRuleKind;
  readonly routeCode: string;
  /** Null when no date of birth is recorded. */
  readonly ageDays: number | null;
}

function ageContains(rule: DoseRule, ageDays: number | null): boolean {
  if (ageDays === null) return rule.ageFromDays === 0 && rule.ageToDays === null;
  return ageDays >= rule.ageFromDays && (rule.ageToDays === null || ageDays < rule.ageToDays);
}

export function weightContains(rule: DoseRule, weightScaled: number): boolean {
  const from = rule.weightFromScaled ?? 0;
  return (
    weightScaled >= from && (rule.weightToScaled === null || weightScaled < rule.weightToScaled)
  );
}

/** Whether a rule's age band is narrower than "every age" (so an estimated age matters). */
export function isAgeBanded(rule: DoseRule): boolean {
  return rule.ageFromDays > 0 || rule.ageToDays !== null;
}

export type DoseRuleSelection<T extends DoseRule> =
  /** No rule of this kind exists for the subject at all. */
  | { readonly kind: 'none' }
  /** Rules exist for the subject and route, but none covers this age (or the age is unknown). */
  | { readonly kind: 'no_band'; readonly ageUnknown: boolean }
  /** The rules that apply (one, or for weight bands every band for this age). */
  | { readonly kind: 'rules'; readonly rules: readonly T[] }
  /** Two rules apply where one should: a configuration error, reported, never guessed. */
  | { readonly kind: 'overlap'; readonly ruleIds: readonly string[] };

/**
 * The rules of one kind that apply. For `weight_band_dose` every band for this age is
 * returned, since the weight picks among them. Retired rules never apply.
 */
export function selectDoseRules<T extends DoseRule>(
  rules: readonly T[],
  query: DoseRuleQuery,
): DoseRuleSelection<T> {
  const forSubject = rules.filter(
    (r) =>
      r.approval !== 'retired' &&
      r.subjectType === query.subjectType &&
      r.subject === query.subject &&
      r.kind === query.kind &&
      (r.routeCode === null || r.routeCode === query.routeCode),
  );
  if (forSubject.length === 0) return { kind: 'none' };
  const applicable = forSubject.filter((r) => ageContains(r, query.ageDays));
  if (applicable.length === 0) return { kind: 'no_band', ageUnknown: query.ageDays === null };
  if (query.kind !== 'weight_band_dose' && applicable.length > 1) {
    return { kind: 'overlap', ruleIds: applicable.map((r) => r.id) };
  }
  return { kind: 'rules', rules: applicable };
}

/** The weight band containing this weight, or an overlap error. */
export function selectWeightBand<T extends DoseRule>(
  bands: readonly T[],
  weightScaled: number,
):
  | { readonly kind: 'band'; readonly rule: T }
  | { readonly kind: 'no_band' }
  | { readonly kind: 'overlap'; readonly ruleIds: readonly string[] } {
  const matching = bands.filter((r) => weightContains(r, weightScaled));
  const [only, ...others] = matching;
  if (only === undefined) return { kind: 'no_band' };
  if (others.length > 0) return { kind: 'overlap', ruleIds: matching.map((r) => r.id) };
  return { kind: 'band', rule: only };
}
